import React from "react";
import { type Location, useLocation } from "react-router-dom";

type LocationHistoryContextType = {
  locationHistory: Location[];
};

const initialEmptyState: LocationHistoryContextType = {
  locationHistory: [],
};

export const MAX_LOCATION_HISTORY_SIZE = 10;

export const LocationHistoryContext =
  React.createContext<LocationHistoryContextType>(initialEmptyState);

/*
 * This provider keeps track of all changes to location.

 * A limitation of v6 react-router is that it does not expose the history stack for you, so we must
 * track it ourselves.
 *
 * Inspiration:
 * https://jasonwatmore.com/react-router-v6-listen-to-location-route-change-without-history-listen#:~:text=The%20new%20React%20Router%20v6,case%20whenever%20the%20location%20changes.
 *
 * A limitation the library is that useLocation() doesn't distinguish between a push and a replace.
 * Hence, the stack we capture here will actually be different to that internal to RR. This
 * shouldn't present a problem for our current usecases, but be aware.
 *
 * Being able to subscribe to the history (as could be done in v5) is a feature request, that likely
 * won't be completed, as the navigation API should in future provide this functionality.
 * More info: https://github.com/remix-run/react-router/issues/9422
 */
export const LocationHistoryProvider: FCwC = ({ children }) => {
  const location = useLocation();
  const [locations, setLocations] = React.useState<Location[]>([]);

  React.useEffect(
    () =>
      setLocations((prevState) => {
        if (location.pathname === prevState[prevState.length - 1]?.pathname) {
          // Take this newer variant of this location - supports pages that navigate to themselves
          // to update search params
          const newList = [...prevState];
          newList[newList.length - 1] = location;
          return newList;
        }

        const newLocation = { ...location };

        const newList = [...prevState, newLocation];
        const trimmedList =
          newList.length > MAX_LOCATION_HISTORY_SIZE
            ? newList.slice(1, MAX_LOCATION_HISTORY_SIZE + 1)
            : newList;

        return trimmedList;
      }),
    [location],
  );

  return (
    <LocationHistoryContext.Provider
      value={{
        // Necessary, to copy else users of the list that call methods like .reverse() can easily,
        // accidentally alter the global list.
        get locationHistory() {
          return [...locations.map((l) => ({ ...l }))];
        },
      }}
    >
      {children}
    </LocationHistoryContext.Provider>
  );
};

export function useLocationHistoryContext() {
  const context = React.useContext(LocationHistoryContext);

  if (context === undefined) {
    throw new Error(
      "useNavigateListener must be used within a LocationListenerProvider",
    );
  }

  return context as LocationHistoryContextType;
}
